import React, { useState, useEffect } from "react";
import { ErrorResponse } from "../../../Models/Error.js";
import { Shipment } from "../../../Models/Shipment.js";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { useNavigate } from "react-router-dom";
import {
  ShipmentDeleteContextValue,
  ShipmentDeleteProviderProps,
} from "./types.ts";
import { ShipmentDeleteContext } from "./contexts.ts";

const shipmentsService = new ShipmentsService();

/* ===========================================================================
 * PROVIDER: ShipmentDeleteProvider
 * =========================================================================== */

/**
 * @component ShipmentDeleteProvider
 *
 * Handles deletion of one or more shipment entities.
 * - Executes multiple deletion requests concurrently.
 * - Tracks loader state and errors.
 * - Exposes delete function via context for child components.
 *
 * @param {ShipmentDeleteProviderProps} props
 */
export const ShipmentDeleteProvider: React.FC<ShipmentDeleteProviderProps> = ({
  children,
  data = [], // Array of shipment IDs to delete
  callback, // Optional callback after successful deletion
}) => {
  const navigate = useNavigate(); // React Router navigation
  const [error, setError] = useState<ErrorResponse<Shipment> | null>(null); // Stores API/validation errors
  const [loader, setLoader] = useState<boolean>(false); // Global loader state

  /**
   * Deletes shipments by ID.
   * - Runs all delete requests concurrently.
   * - Updates error state if any deletion fails.
   * - Calls callback and navigates if all succeed.
   */
  const deleteShipments = async (): Promise<void> => {
    setLoader(true);

    try {
      const results = await Promise.allSettled(
        data.map((id) => shipmentsService.delete(id)),
      );

      // Separate results into successful and rejected categories
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      // If any deletions failed
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Shipment>;
        setError(firstError);

        // If some deletions succeeded, call callback to refresh state/UI
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // If all deletions succeeded
      if (callback) callback();
      navigate("/shipments"); // Navigate back to shipments list
    } catch (unexpected) {
      // Fallback for unexpected errors
      const err: ErrorResponse<Shipment> = {
        response: { data: {} },
      };
      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECT: show error toast when deletion fails
   * ------------------------------------------------------------------------- */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /* =========================================================================
   * CONTEXT VALUE
   * ======================================================================= */
  const contextValue: ShipmentDeleteContextValue = {
    data,
    loader,
    deleteShipments,
    callback,
  };

  return (
    <ShipmentDeleteContext.Provider value={contextValue}>
      {children}
    </ShipmentDeleteContext.Provider>
  );
};
